import React, { useEffect, useMemo, useState } from 'react';
import { Box, Container, Typography, Card, CardContent, Button, CircularProgress, Alert, Chip, Divider } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import { useAuth } from '../../context/AuthContext';
import { listMyProgramMemberships, listProgramSeasonTeams, getProgram } from '../../services/programService';
import { programRoleLabel, programRoleChipStyle } from '../../utils/programRoles';

// Phase C.4a — this page is now a lighter Program overview/launch point,
// not a second competing Team directory. The full card-per-Team
// Setup/Current/History listing (present since 027) has been removed —
// those exact lifecycle badges (seasonStatusLabel/seasonStatusChipStyle)
// now live naturally on each Team's own row inside Seasons & Divisions
// (ProgramSeasonsPage.js), which is also where "+ Add Team" now lives.
// The concise count line below is kept — a one-line operational signal
// is still useful; a full duplicate directory was not. Setup/Current/
// History remain PRESENTATION groupings only (computed here from teams.
// season_status, 022/027) — never a stored concept — so nothing about
// removing this view loses any real data.

function ProgramDashboardInner() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [program, setProgram] = useState(null);
  // Phase C.2b — fetched separately from list_my_program_memberships()'s
  // own `program` state, since that RPC (025) doesn't return slug (same
  // reasoning as MyProgramsPage.js's own listProgramSlugsByIds() — a
  // plain client-side read of the already-authenticated-readable
  // `programs` table, not a new RPC/migration). null/absent means "no
  // public slug yet" — the View Public Page action simply doesn't render.
  const [programSlug, setProgramSlug] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Set by ManageProgramPage after a completed ownership transfer where
  // the outgoing Owner chose to retain Program Admin access — same
  // capture-once + consumed-navigation-state pattern as TeamDetailPage's
  // justCreatedInProgram banner.
  const [transferredOwnershipTo] = useState(() => location.state?.transferredOwnershipTo ?? null);
  // Set by TeamDetailPage after a Setup Team was permanently deleted.
  const [deletedSetupTeam] = useState(() => location.state?.deletedSetupTeam ?? null);

  useEffect(() => {
    if (location.state?.transferredOwnershipTo || location.state?.deletedSetupTeam) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    // Reuses list_my_programs() (023) rather than a second, program-
    // specific RPC — its own WHERE clause is already exactly the
    // authorization boundary this dashboard needs (an active Program
    // Admin for this program), and a Program Admin realistically
    // administers very few Programs, so finding the one matching
    // programId client-side is simpler than adding another RPC.
    //
    // Guards on currentUser (not just relies on RequireAuth) so this is
    // genuinely re-invoked whenever the authenticated identity changes
    // while the component stays mounted on the same /my-programs/
    // :programId URL — otherwise a result fetched under a PREVIOUS
    // session (e.g. a co-Program-Admin who wasn't the one this page was
    // first loaded for) could persist stale in React state instead of
    // being re-evaluated for the new session. Every field on this page —
    // the Program Admin badge, the Manage Program link, the Season Team
    // list — comes from the SAME `program`/`teams` state, so this one
    // fix covers all of it.
    if (!currentUser) return;
    setLoading(true);
    Promise.all([listMyProgramMemberships(), listProgramSeasonTeams(programId)])
      .then(([programs, seasonTeams]) => {
        setProgram(programs.find(p => p.id === programId) || null);
        setTeams(seasonTeams);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));

    // Best-effort, independent of the load above — a slug-lookup failure
    // must never block the dashboard itself; it just means no View
    // Public Page action renders.
    getProgram(programId).then(p => setProgramSlug(p?.slug || null)).catch(() => setProgramSlug(null));
  }, [programId, currentUser]);

  // Setup/Current/History follow teams.season_status directly (027) —
  // deliberately NOT teams.status (active/archived), which is a
  // completely independent, manual admin-visibility toggle (009) and
  // must never be conflated with the competitive-season lifecycle.
  const setupTeams = useMemo(() => teams.filter(t => t.season_status === 'setup'), [teams]);
  const currentTeams = useMemo(
    () => teams.filter(t => t.season_status === 'preseason' || t.season_status === 'active' || t.season_status === 'closeout'),
    [teams]
  );
  const historyTeams = useMemo(() => teams.filter(t => t.season_status === 'ended'), [teams]);

  if (loading) return <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>;
  if (error) return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">{error}</Alert></Container>;
  if (!program) return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Alert severity="error">Program not found, or you don't have Program Admin access to it.</Alert>
    </Container>
  );

  const serviceAreaLine = [program.primary_city, program.primary_state_code].filter(Boolean).join(', ');

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button onClick={() => navigate('/my-programs')} sx={{ textTransform: 'none', color: '#6a8aa8', mb: 1, pl: 0 }}>
        ← My Programs
      </Button>

      {transferredOwnershipTo && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Ownership of {program.name} has been transferred to {transferredOwnershipTo}. You remain a Program Administrator.
        </Alert>
      )}
      {deletedSetupTeam && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {deletedSetupTeam} was permanently deleted.
        </Alert>
      )}

      <Typography variant="h4" sx={{ fontWeight: 900, textTransform: 'uppercase', letterSpacing: '-0.01em' }}>
        {program.name}
      </Typography>
      {serviceAreaLine && (
        <Typography sx={{ color: '#9eb8d0', mt: 0.5 }}>{serviceAreaLine}</Typography>
      )}
      {program.primary_county_name && (
        <Typography sx={{ color: '#6a8aa8', fontSize: '0.9rem' }}>{program.primary_county_name} County</Typography>
      )}
      {/* Program Owner / Program Admin are Program-scoped, distinct from
          the Season-Team-scoped Team Admin / Head Coach roles below —
          see utils/teamRoles.js. Reflects the backend's actual authority
          result (program.viewer_role, from list_my_program_memberships())
          — never inferred client-side from created_by, admin order, or
          any other heuristic. Never labeled Head Coach here, even though
          creating a Season Team also grants a team_admin bridge row. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
        <Chip size="small" label={programRoleLabel(program.viewer_role)} sx={{ fontWeight: 700, ...programRoleChipStyle(program.viewer_role) }} />
        <Button size="small" onClick={() => navigate(`/my-programs/${programId}/manage`)} sx={{ textTransform: 'none', fontWeight: 700 }}>
          Manage Program →
        </Button>
        {/* Phase C.1 — Program/Season/Division/Team hierarchy admin view,
            a separate page from this dashboard's own season_status
            grouping (see ProgramSeasonsPage.js's own header). */}
        <Button size="small" onClick={() => navigate(`/my-programs/${programId}/seasons`)} sx={{ textTransform: 'none', fontWeight: 700 }}>
          Seasons &amp; Divisions →
        </Button>
        {/* Phase C.2b — compact, visually secondary/subtle action (muted
            color, no bold weight) so it reads clearly as a DIFFERENT
            kind of destination than the two management actions above —
            never rendered at all when this Program has no public slug
            yet, so there is never a dead public link here. */}
        {programSlug && (
          <Button size="small" onClick={() => navigate(`/programs/${programSlug}`)} sx={{ textTransform: 'none', fontWeight: 500, color: '#6a8aa8' }}>
            View Public Page ↗
          </Button>
        )}
      </Box>

      {teams.length > 0 && (
        <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem', mt: 2 }}>
          {setupTeams.length} Setup · {currentTeams.length} Current · {historyTeams.length} History
        </Typography>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Phase C.4a — the full card-per-Team directory that used to live
          here is gone; Seasons & Divisions is now where Teams are built,
          viewed in their real Program → Season → Division structure, and
          managed (Open Team/Team Settings/Move/Archive on each row).
          This empty state now points there directly instead of offering
          its own separate Team-creation entry point. */}
      {teams.length === 0 && (
        <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Typography sx={{ color: '#9eb8d0', mb: 2 }}>No Season Teams yet.</Typography>
            <Button
              variant="contained" color="primary"
              onClick={() => navigate(`/my-programs/${programId}/seasons`)}
              sx={{ textTransform: 'none', fontWeight: 800, px: 3 }}
            >
              Build in Seasons &amp; Divisions →
            </Button>
          </CardContent>
        </Card>
      )}
    </Container>
  );
}

export default function ProgramDashboardPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth><ProgramDashboardInner /></RequireAuth>
    </Box>
  );
}
